<template>
  <q-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" maximized>
    <q-card class="disc-card">
      <q-bar class="bg-deep-orange text-white disc-main-bar">
        <q-icon name="compare_arrows" size="sm" />
        <div class="disc-main-titles">
          <span v-if="projectName" class="disc-main-project" :title="projectName">
            <q-icon name="apartment" size="11px" class="q-mr-xs" />{{ projectName }}
          </span>
          <span class="disc-main-title">Сравнение: Наши данные ↔ УСП</span>
        </div>
        <q-space />
        <div class="row q-gutter-sm items-center q-mr-md" v-if="!data.loading">
          <q-badge color="white" text-color="grey-8" class="q-pa-xs">
            Всего: {{ data.rows.length }}
          </q-badge>
          <q-badge color="orange-3" text-color="orange-10" class="q-pa-xs" v-if="discSummary.diffCount">
            ⚠ Факт≠УСП {{ discSummary.diffCount }}
          </q-badge>
          <q-badge color="cyan-2" text-color="cyan-10" class="q-pa-xs" v-if="discSummary.smetaDiffCount">
            ⚠ Факт≠Смета {{ discSummary.smetaDiffCount }}
          </q-badge>
          <q-badge color="green-2" text-color="green-10" class="q-pa-xs" v-if="discSummary.smetaVsUspSmetaCount">
            ⚠ Смета≠УСП-смета {{ discSummary.smetaVsUspSmetaCount }}
          </q-badge>
          <q-badge color="blue-2" text-color="blue-10" class="q-pa-xs" v-if="discSummary.onlyOurs">
            ← {{ discSummary.onlyOurs }}
          </q-badge>
          <q-badge color="purple-2" text-color="purple-10" class="q-pa-xs" v-if="discSummary.onlyUsp">
            → {{ discSummary.onlyUsp }}
          </q-badge>
          <q-badge color="green-2" text-color="green-10" class="q-pa-xs">
            ✓ {{ discSummary.matchCount }}
          </q-badge>
        </div>
        <q-btn dense flat icon="close" v-close-popup />
      </q-bar>

      <div class="disc-filters q-pa-sm row items-center q-gutter-sm">
        <q-btn
          icon="refresh"
          label="Обновить УСП"
          flat dense no-caps size="sm"
          color="deep-orange"
          :loading="data.refreshing"
          @click="$emit('refresh')"
        >
          <q-tooltip>Запросить свежие данные из УСП</q-tooltip>
        </q-btn>
        <q-separator vertical class="q-mx-xs" />
        <div v-if="canAction" class="cron-btn-group">
          <div class="row items-center no-wrap q-gutter-xs">
            <q-btn
              icon="send"
              label="Отправить смету"
              flat dense no-caps size="sm"
              color="teal-7"
              :loading="cronLoading.smeta"
              @click="$emit('cron-update-smeta')"
            >
              <q-tooltip>Запустить sbs.cron__usp_update_smeta_v2</q-tooltip>
            </q-btn>
            <q-btn
              icon="history"
              flat dense round size="xs"
              color="teal-7"
              @click="$emit('cron-history', 'estimate')"
            >
              <q-tooltip>История отправки сметы</q-tooltip>
            </q-btn>
          </div>
          <div v-if="lastRun.smeta" class="cron-last-run" :class="lastRun.smeta.ok ? 'cron-last-run--ok' : 'cron-last-run--err'">
            <q-icon :name="lastRun.smeta.ok ? 'check_circle' : 'error'" size="11px" />
            <span>{{ lastRun.smeta.time }}</span>
            <q-tooltip v-if="lastRun.smeta.message" max-width="400px">{{ lastRun.smeta.message }}</q-tooltip>
          </div>
        </div>
        <div v-if="canAction" class="cron-btn-group">
          <div class="row items-center no-wrap q-gutter-xs">
            <q-btn
              icon="description"
              label="Отправить договора"
              flat dense no-caps size="sm"
              color="indigo-7"
              :loading="cronLoading.contracts"
              @click="$emit('cron-create-contracts')"
            >
              <q-tooltip>Запустить sbs.cron__usp_create_contracts_v2</q-tooltip>
            </q-btn>
            <q-btn
              icon="history"
              flat dense round size="xs"
              color="indigo-7"
              @click="$emit('cron-history', 'contract')"
            >
              <q-tooltip>История отправки договоров</q-tooltip>
            </q-btn>
          </div>
          <div v-if="lastRun.contracts" class="cron-last-run" :class="lastRun.contracts.ok ? 'cron-last-run--ok' : 'cron-last-run--err'">
            <q-icon :name="lastRun.contracts.ok ? 'check_circle' : 'error'" size="11px" />
            <span>{{ lastRun.contracts.time }}</span>
            <q-tooltip v-if="lastRun.contracts.message" max-width="400px">{{ lastRun.contracts.message }}</q-tooltip>
          </div>
        </div>

        <!-- История запусков -->
        <q-dialog v-model="historyDialog.show">
          <q-card style="min-width:520px; max-width:700px">
            <q-bar class="bg-grey-8 text-white" style="height:36px">
              <q-icon name="history" size="sm" />
              <span class="q-ml-sm text-subtitle2">
                История: {{ historyDialog.logType === 'estimate' ? 'Отправка сметы' : 'Отправка договоров' }}
              </span>
              <q-space />
              <q-btn dense flat icon="close" v-close-popup />
            </q-bar>
            <q-card-section class="q-pa-sm">
              <div v-if="historyDialog.loading" class="flex flex-center q-pa-lg">
                <q-spinner-dots color="grey-7" size="2em" />
              </div>
              <table v-else class="cron-hist-table">
                <thead>
                  <tr>
                    <th style="width:90px">Статус</th>
                    <th style="width:120px">Дата/время</th>
                    <th>Ошибка</th>
                    <th style="width:80px">Запрос</th>
                    <th>Ответ УСП</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!historyDialog.rows.length">
                    <td colspan="5" class="text-center text-grey q-pa-md">Нет данных</td>
                  </tr>
                  <tr v-for="(r, i) in historyDialog.rows" :key="i" :class="r.status === 'SUCCESS' ? 'cron-hist-ok' : 'cron-hist-err'">
                    <td class="text-center">
                      <q-badge :color="r.status === 'SUCCESS' ? 'positive' : 'negative'" :label="r.status" />
                    </td>
                    <td class="text-caption" style="white-space:nowrap">{{ fmtHistDt(r.finished_at || r.created_at) }}</td>
                    <td class="cron-hist-error text-caption">{{ r.error_message || '—' }}</td>
                    <td class="text-center">
                      <q-btn
                        v-if="r.log_id"
                        icon="code"
                        flat dense round size="xs"
                        color="blue-grey-6"
                        @click="$emit('cron-log-json', r.log_id, historyDialog.logType)"
                      >
                        <q-tooltip>Посмотреть JSON запроса</q-tooltip>
                      </q-btn>
                      <span v-else class="text-grey-5">—</span>
                    </td>
                    <td class="cron-hist-response text-caption">{{ r.response_text || '—' }}</td>
                  </tr>
                </tbody>
              </table>

              <!-- JSON viewer -->
              <q-dialog v-model="jsonViewDialog.show">
                <q-card style="min-width:600px; max-width:900px; max-height:80vh">
                  <q-bar class="bg-blue-grey-8 text-white" style="height:36px">
                    <q-icon name="code" size="sm" />
                    <span class="q-ml-sm text-subtitle2">JSON запроса</span>
                    <q-space />
                    <q-btn dense flat icon="content_copy" size="sm" @click="copyJson">
                      <q-tooltip>Скопировать</q-tooltip>
                    </q-btn>
                    <q-btn dense flat icon="close" v-close-popup />
                  </q-bar>
                  <q-card-section class="q-pa-sm" style="overflow:auto; max-height:calc(80vh - 36px)">
                    <div v-if="jsonViewDialog.loading" class="flex flex-center q-pa-xl">
                      <q-spinner-dots color="blue-grey-6" size="2em" />
                    </div>
                    <pre v-else class="cron-json-pre">{{ jsonViewDialog.content }}</pre>
                  </q-card-section>
                </q-card>
              </q-dialog>
            </q-card-section>
          </q-card>
        </q-dialog>

        <!-- Список работ группы -->
        <q-dialog v-model="worksDialog.show">
          <q-card style="min-width:520px; max-width:800px; max-height:80vh">
            <q-bar class="bg-blue-grey-7 text-white" style="height:36px">
              <q-icon name="list" size="sm" />
              <span class="q-ml-sm text-subtitle2 ellipsis" style="max-width:600px">
                Список работ{{ worksDialog.title ? ': ' + worksDialog.title : '' }}
              </span>
              <q-badge color="white" text-color="blue-grey-8" class="q-ml-sm">
                {{ worksDialog.items.length }}
              </q-badge>
              <q-space />
              <q-btn dense flat icon="close" v-close-popup />
            </q-bar>
            <q-card-section class="q-pa-sm" style="overflow:auto; max-height:calc(80vh - 36px)">
              <table class="disc-work-mini-tbl">
                <thead>
                  <tr>
                    <th class="dwm-th-num">#</th>
                    <th class="dwm-th-id">ID</th>
                    <th class="dwm-th-name">Название работы</th>
                    <th class="dwm-th-fm">floor_map_id</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!worksDialog.items.length">
                    <td colspan="4" class="text-center text-grey q-pa-md">Нет данных</td>
                  </tr>
                  <tr v-for="(it, i) in worksDialog.items" :key="(it.work_set_id || '') + '_' + i">
                    <td class="dwm-td-num">{{ i + 1 }}</td>
                    <td class="dwm-td-id">{{ it.work_set_id ?? '—' }}</td>
                    <td class="dwm-td-name">{{ it.work_set_name || '—' }}</td>
                    <td class="dwm-td-fm">
                      <span v-if="(it.floor_map_ids || []).length" :title="(it.floor_map_ids || []).join(', ')">
                        <span class="dwm-fm-cnt" v-if="(it.floor_map_ids || []).length > 1">×{{ it.floor_map_ids.length }}</span>
                        {{ (it.floor_map_ids || []).join(', ') }}
                      </span>
                      <span v-else class="text-grey-5">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </q-card-section>
          </q-card>
        </q-dialog>

        <q-separator vertical class="q-mx-xs" />
        <q-select
          v-model="data.filterType"
          :options="discTypeOptions"
          label="Тип проекта"
          dense outlined clearable
          style="min-width:180px"
        />
        <q-select
          v-model="data.filterBlock"
          :options="discBlockOptions"
          label="Блок"
          dense outlined clearable
          style="min-width:130px"
        />
        <q-select
          v-model="data.filterFloor"
          :options="discFloorOptions"
          label="Этаж"
          dense outlined clearable emit-value map-options
          style="min-width:90px"
        />
        <q-input
          v-model="data.search"
          label="Поиск"
          dense outlined clearable
          style="min-width:160px"
        >
          <template v-slot:prepend><q-icon name="search" /></template>
        </q-input>
        <q-space />
      </div>

      <div class="disc-tabs-row row items-center q-gutter-sm q-px-sm q-py-xs">
        <q-tabs v-model="activeTab" dense no-caps active-color="deep-orange" indicator-color="deep-orange" align="left" class="disc-tabs">
          <q-tab name="all" :label="`Все (${baseFilteredRows.length})`" />
          <q-tab name="issues" :label="`Расхождения (${discIssuesCount})`" />
          <q-tab name="ok" :label="`Совпадают (${discOkCount})`" />
          <q-tab name="multi" v-if="multiProjectCount > 0">
            <template v-slot:default>
              <div class="row items-center no-wrap q-gutter-xs">
                <q-icon name="call_split" size="14px" color="deep-orange" />
                <span>×Проектов ({{ multiProjectCount }})</span>
              </div>
            </template>
          </q-tab>
        </q-tabs>
        <q-select
          v-if="activeTab === 'issues'"
          v-model="issueSubFilter"
          :options="issueSubFilterOptions"
          dense outlined emit-value map-options
          style="min-width:280px"
          class="q-ml-sm"
        >
          <template v-slot:option="scope">
            <q-item v-bind="scope.itemProps" dense :class="scope.opt.bgClass">
              <q-item-section avatar style="min-width:28px">
                <q-icon :name="scope.opt.icon" :color="scope.opt.iconColor" size="18px" />
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ scope.opt.label }}</q-item-label>
              </q-item-section>
              <q-item-section side v-if="scope.opt.count != null">
                <q-badge :color="scope.opt.badgeColor || 'grey-4'" :text-color="scope.opt.badgeText || 'grey-8'" :label="scope.opt.count" />
              </q-item-section>
            </q-item>
          </template>
          <template v-slot:selected-item="scope">
            <div class="row items-center no-wrap q-gutter-xs">
              <q-icon :name="scope.opt.icon" :color="scope.opt.iconColor" size="16px" />
              <span>{{ scope.opt.label }}</span>
            </div>
          </template>
        </q-select>
        <q-space />
        <q-btn-toggle
          v-if="canAction"
          v-model="bulkMode"
          clearable
          dense no-caps size="xs"
          toggle-color="deep-orange"
          text-color="grey-7"
          class="disc-mode-toggle"
          :options="[
            { label: 'Факт → Смета', value: 'smeta', icon: 'tune' },
            { label: '→ УСП', value: 'usp', icon: 'send' },
            { label: 'Сторно', value: 'storno', icon: 'undo' },
            { label: 'Смета → УСП', value: 'smeta_to_usp', icon: 'playlist_add_check' },
          ]"
        />
        <q-checkbox
          v-if="canAction && bulkMode === 'usp'"
          v-model="uspAggregate"
          dense size="xs" color="deep-orange"
          label="Свести к одной дате"
          class="q-ml-sm"
        >
          <q-tooltip>
            По умолчанию отправка идёт по каждой дате отдельно.<br>
            Включите, чтобы отправить одной агрегированной записью на последнюю дату (старое поведение).
          </q-tooltip>
        </q-checkbox>
        <q-btn
          v-if="canAction && bulkMode && selectedRows.length"
          dense flat no-caps size="sm"
          :icon="bulkActionIcon"
          :color="applicableSelectedRows.length ? 'deep-orange' : 'grey-5'"
          :disable="!applicableSelectedRows.length"
          :label="bulkActionLabel"
          class="q-ml-sm"
          @click="executeBulkAction"
        >
          <q-tooltip v-if="!applicableSelectedRows.length">
            Ни одна из {{ selectedRows.length }} выбранных строк не подходит для этого действия
          </q-tooltip>
        </q-btn>
        <span class="text-caption text-grey-7 q-ml-sm">
          Показано {{ discFilteredRows.length }} из {{ baseFilteredRows.length }}
        </span>
      </div>

      <div class="disc-scroll">
        <table class="disc-table">
          <thead>
            <tr>
              <th rowspan="2" class="disc-th-info disc-th-check" style="width:32px">
                <q-checkbox
                  v-if="bulkMode"
                  :model-value="isAllSelected"
                  @update:model-value="toggleAll"
                  size="xs" dense color="deep-orange"
                />
              </th>
              <th rowspan="2" class="disc-th-info" style="width:12%">Объект</th>
              <th rowspan="2" class="disc-th-info" style="width:25%">Работа</th>
              <th colspan="2" class="disc-th-smeta-group">СМЕТА (₸)</th>
              <th colspan="2" class="disc-th-operfact">ОПЕРФАКТ (₸)</th>
              <th rowspan="2" class="disc-th-delta" style="width:11%">Разница (₸)</th>
              <th rowspan="2" class="disc-th-delta" style="width:4%">%</th>
              <th rowspan="2" class="disc-th-info" style="width:8%">Отправлено</th>
            </tr>
            <tr>
              <th class="disc-th-smeta" style="width:10%">Наша</th>
              <th class="disc-th-smeta-usp" style="width:10%">УСП</th>
              <th class="disc-th-ours" style="width:10%">Наша</th>
              <th class="disc-th-usp" style="width:10%">УСП</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="data.loading">
              <td colspan="10" class="q-pa-lg">
                <div class="flex flex-center column" style="min-height:120px">
                  <q-spinner-dots size="3em" color="deep-orange" />
                  <div class="q-mt-sm text-grey">Загрузка данных сравнения...</div>
                </div>
              </td>
            </tr>
            <tr v-else-if="!discFilteredRows.length">
              <td colspan="10" class="text-center q-pa-lg text-grey">
                <q-icon name="inbox" size="2em" class="q-mb-sm" /><br>
                Нет данных для отображения
              </td>
            </tr>
            <template v-for="(r, i) in discFilteredRows" :key="rowKey(r)">
            <tr :class="discRowClass(r)">
              <td class="disc-cell-check" @click.stop>
                <q-checkbox
                  v-if="bulkMode"
                  :model-value="!!selectedMap[rowKey(r)]"
                  @update:model-value="toggleRow(r)"
                  size="xs" dense color="deep-orange"
                />
              </td>

              <!-- Объект -->
              <td class="disc-cell-obj">
                <div class="disc-obj-row">
                  <q-icon v-if="r.source === 'only_ours'" name="arrow_back" size="14px" color="blue-5" class="disc-obj-icon" />
                  <q-icon v-else-if="r.source === 'only_usp'" name="arrow_forward" size="14px" color="purple-5" class="disc-obj-icon" />
                  <q-icon v-else-if="r.has_discrepancy" name="warning" size="14px" color="orange-8" class="disc-obj-icon" />
                  <q-icon v-else name="check_circle" size="14px" color="green-5" class="disc-obj-icon" />
                  <span class="disc-obj-type">{{ r.project_type_name || '—' }}</span>
                </div>
                <div class="disc-obj-meta">
                  <span v-if="r.block_name">Бл.{{ r.block_name }}</span>
                  <span v-if="r.level_num"> / Эт.{{ r.level_num }}</span>
                </div>
                <div v-if="isMultiProject(r)" class="disc-obj-multi" @click.stop="toggleExpand(r)">
                  <q-icon :name="isExpanded(r) ? 'expand_less' : 'expand_more'" size="12px" />
                  <span>×{{ r.proj_cnt }}</span>
                </div>
              </td>

              <!-- Работа: % + короткое название -->
              <td class="disc-cell-work">
                <div class="disc-work-pct-row">
                  <span class="disc-work-pct" :class="pctClass(parsePct(r.group_name))">
                    {{ parsePct(r.group_name) != null ? parsePct(r.group_name) + '%' : '—' }}
                  </span>
                  <span
                    class="disc-work-name"
                    :title="workSetFullTitle(r)"
                  >
                    {{ workSetShortName(r) }}
                  </span>
                  <q-btn
                    v-if="workSetItems(r).length > 1"
                    flat dense no-caps size="xs"
                    icon="list"
                    color="blue-grey-7"
                    :label="String(workSetItems(r).length)"
                    class="disc-works-btn"
                    @click.stop="openWorksDialog(r)"
                  >
                    <q-tooltip>Список работ ({{ workSetItems(r).length }})</q-tooltip>
                  </q-btn>
                  <q-btn
                    flat dense round size="xs"
                    icon="read_more" color="deep-orange"
                    class="disc-detail-btn"
                    @click.stop="$emit('open-detail', r)"
                  >
                    <q-tooltip>Детали по периодам</q-tooltip>
                  </q-btn>
                </div>
                <div class="disc-work-group-name" v-if="parseGroupLabel(r.group_name)" :title="parseGroupLabel(r.group_name)">{{ parseGroupLabel(r.group_name) }}</div>
                <div v-if="r.work_guid" class="disc-work-guid" @click.stop>
                  <span :title="r.work_guid">{{ (r.work_guid || '').substring(0, 8) }}…</span>
                  <q-btn flat dense round size="xs" icon="content_copy" class="disc-guid-copy"
                    @click.stop="copyToClipboard(r.work_guid)">
                    <q-tooltip>Скопировать GUID</q-tooltip>
                  </q-btn>
                </div>
              </td>

              <!-- Наша смета -->
              <td class="disc-cell-money disc-cell-smeta">
                <div class="disc-money-total">{{ fmtDisc(r.our_smeta_total) }}</div>
                <div class="disc-money-detail">
                  <span>СМР {{ fmtDisc(r.our_smeta_smr) }}</span>
                  <span>ТМЦ {{ fmtDisc(r.our_smeta_mat) }}</span>
                </div>
              </td>

              <!-- Смета УСП -->
              <td class="disc-cell-money disc-cell-smeta-usp">
                <div class="disc-money-total">{{ fmtDisc(r.usp_smeta_total) }}</div>
                <div class="disc-money-detail">
                  <span>СМР {{ fmtDisc(r.usp_smeta_smr) }}</span>
                  <span>ТМЦ {{ fmtDisc(r.usp_smeta_mat) }}</span>
                </div>
                <div
                  v-if="(r.usp_smeta_total || 0) > 0 && (r.our_smeta_total || 0) > 0"
                  class="disc-smeta-delta-block"
                >
                  <span :class="smetaDeltaClass(r.our_smeta_smr - r.usp_smeta_smr)">
                    Δ СМР {{ fmtDiscDelta(r.our_smeta_smr - r.usp_smeta_smr) }}
                  </span>
                  <span :class="smetaDeltaClass(r.our_smeta_mat - r.usp_smeta_mat)">
                    Δ ТМЦ {{ fmtDiscDelta(r.our_smeta_mat - r.usp_smeta_mat) }}
                  </span>
                </div>
              </td>

              <!-- Наши данные -->
              <td class="disc-cell-money disc-cell-ours">
                <div class="disc-money-total">{{ fmtDisc(r.our_total) }}</div>
                <div class="disc-money-detail">
                  <span>СМР {{ fmtDisc(r.our_smr) }}</span>
                  <span>ТМЦ {{ fmtDisc(r.our_materials) }}</span>
                </div>
                <div v-if="(r.our_smeta_total || 0) > 0" class="disc-smeta-delta-block">
                  <span :class="smetaDeltaClass(r.our_smr - r.our_smeta_smr)">Δ СМР {{ fmtDiscDelta(r.our_smr - r.our_smeta_smr) }}</span>
                  <span :class="smetaDeltaClass(r.our_materials - r.our_smeta_mat)">Δ ТМЦ {{ fmtDiscDelta(r.our_materials - r.our_smeta_mat) }}</span>
                  <q-btn
                    v-if="canAction && hasSmetaDiff(r)"
                    dense flat no-caps size="xs"
                    icon="tune" label="Факт по смете"
                    color="red-7"
                    class="disc-align-btn"
                    @click.stop="$emit('align-to-smeta', r)"
                  >
                    <q-tooltip>Пересоздать факт из данных сметы</q-tooltip>
                  </q-btn>
                </div>
              </td>

              <!-- Данные УСП -->
              <td class="disc-cell-money disc-cell-usp">
                <div class="disc-money-total">{{ fmtDisc(r.usp_total) }}</div>
                <div class="disc-money-detail">
                  <span>СМР {{ fmtDisc(r.usp_smr) }}</span>
                  <span>ТМЦ {{ fmtDisc(r.usp_materials) }}</span>
                </div>
              </td>

              <!-- Разница -->
              <td class="disc-cell-money" :class="discDeltaClass(r.delta_total)">
                <div class="disc-money-total">{{ fmtDiscDelta(r.delta_total) }}</div>
                <div class="disc-money-detail">
                  <span>СМР {{ fmtDiscDelta(r.delta_smr) }}</span>
                  <span>ТМЦ {{ fmtDiscDelta(r.delta_materials) }}</span>
                </div>
              </td>

              <!-- % -->
              <td class="text-center text-weight-medium" :class="discDeltaClass(r.delta_pct)">
                {{ r.delta_pct != null ? (r.delta_pct > 0 ? '+' : '') + r.delta_pct + '%' : '—' }}
              </td>

              <!-- Отправлено -->
              <td class="text-center text-caption">{{ discFmtDate(r.last_sent_at) }}</td>
            </tr>

            <!-- Разбивка по project_id (мульти-проектная работа) -->
            <tr v-if="isMultiProject(r) && isExpanded(r)" class="disc-breakdown-row">
              <td colspan="10">
                <div class="disc-breakdown">
                  <div class="disc-breakdown-title">
                    <q-icon name="call_split" size="14px" color="deep-orange" />
                    Разбивка по проектам для work_guid
                    <span class="disc-breakdown-guid">{{ (r.work_guid || '').substring(0,8) }}…</span>
                  </div>
                  <table class="disc-breakdown-table">
                    <thead>
                      <tr>
                        <th style="width:30%">Объект</th>
                        <th class="disc-bt-smeta" style="width:17%">Наша смета (СМР / ТМЦ / Итого)</th>
                        <th class="disc-bt-fact"  style="width:17%">Наш факт (СМР / ТМЦ / Итого)</th>
                        <th class="disc-bt-delta" style="width:17%">Δ Факт − Смета</th>
                        <th style="width:19%">project_id</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(p, idx) in (r.project_breakdown || [])" :key="idx">
                        <td>
                          <div class="disc-bd-type">{{ p.project_type_name || '—' }}</div>
                          <div class="disc-bd-meta">
                            <span v-if="p.block_name">{{ p.block_name }}</span>
                            <span v-if="p.level_nums"> / Эт.{{ p.level_nums }}</span>
                          </div>
                        </td>
                        <td class="disc-bt-smeta">
                          <span>{{ fmtDisc(p.our_smeta_smr) }}</span>
                          <span class="disc-bd-sep">/</span>
                          <span>{{ fmtDisc(p.our_smeta_mat) }}</span>
                          <span class="disc-bd-sep">/</span>
                          <b>{{ fmtDisc(p.our_smeta_total) }}</b>
                        </td>
                        <td class="disc-bt-fact">
                          <span>{{ fmtDisc(p.our_smr) }}</span>
                          <span class="disc-bd-sep">/</span>
                          <span>{{ fmtDisc(p.our_materials) }}</span>
                          <span class="disc-bd-sep">/</span>
                          <b>{{ fmtDisc(p.our_total) }}</b>
                        </td>
                        <td class="disc-bt-delta">
                          <span :class="smetaDeltaClass(p.our_total - p.our_smeta_total)">
                            {{ fmtDiscDelta(p.our_total - p.our_smeta_total) }}
                          </span>
                        </td>
                        <td class="disc-bd-pid">{{ p.project_id }}</td>
                      </tr>
                      <tr v-if="!(r.project_breakdown || []).length">
                        <td colspan="5" class="text-center text-grey q-pa-sm">
                          Нет разбивки — у работы нет записей в факте.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div class="disc-footer">
        <div class="disc-footer-row">
          <div class="disc-footer-label">ИТОГО по {{ discFilteredRows.length }} строкам:</div>
          <div class="disc-footer-group disc-footer-smeta">
            <span class="disc-footer-title">Наша смета</span>
            <span>СМР: <b>{{ fmtDisc(discSummary.osm_smr) }}</b></span>
            <span>ТМЦ: <b>{{ fmtDisc(discSummary.osm_mat) }}</b></span>
            <span>Итого: <b>{{ fmtDisc(discSummary.osm_total) }}</b></span>
          </div>
          <div class="disc-footer-group disc-footer-smeta-usp">
            <span class="disc-footer-title">Смета УСП</span>
            <span>СМР: <b>{{ fmtDisc(discSummary.usm_smr) }}</b></span>
            <span>ТМЦ: <b>{{ fmtDisc(discSummary.usm_mat) }}</b></span>
            <span>Итого: <b>{{ fmtDisc(discSummary.usm_total) }}</b></span>
          </div>
          <div class="disc-footer-group disc-footer-ours">
            <span class="disc-footer-title">Наши данные</span>
            <span>СМР: <b>{{ fmtDisc(discSummary.our_smr) }}</b></span>
            <span>ТМЦ: <b>{{ fmtDisc(discSummary.our_mat) }}</b></span>
            <span>Итого: <b>{{ fmtDisc(discSummary.our_total) }}</b></span>
          </div>
          <div class="disc-footer-group disc-footer-usp">
            <span class="disc-footer-title">Данные УСП</span>
            <span>СМР: <b>{{ fmtDisc(discSummary.usp_smr) }}</b></span>
            <span>ТМЦ: <b>{{ fmtDisc(discSummary.usp_mat) }}</b></span>
            <span>Итого: <b>{{ fmtDisc(discSummary.usp_total) }}</b></span>
          </div>
          <div class="disc-footer-group disc-footer-delta">
            <span class="disc-footer-title">Разница</span>
            <span :class="discDeltaClass(discSummary.d_smr)">СМР: <b>{{ fmtDiscDelta(discSummary.d_smr) }}</b></span>
            <span :class="discDeltaClass(discSummary.d_mat)">ТМЦ: <b>{{ fmtDiscDelta(discSummary.d_mat) }}</b></span>
            <span :class="discDeltaClass(discSummary.d_total)" class="text-weight-bold">Итого: <b>{{ fmtDiscDelta(discSummary.d_total) }}</b></span>
          </div>
        </div>
      </div>
    </q-card>
  </q-dialog>
</template>

<script>
import { useFormatters } from '../composables/useFormatters';

export default {
  name: 'PpmDiscrepancyDialog',

  props: {
    modelValue: Boolean,
    data: Object,
    canAction: { type: Boolean, default: false },
    projectName: { type: String, default: '' },
  },

  emits: ['update:modelValue', 'open-detail', 'refresh', 'align-to-smeta', 'align-to-smeta-bulk', 'align-to-usp-bulk', 'storno-bulk', 'send-smeta-bulk', 'cron-update-smeta', 'cron-create-contracts', 'cron-history', 'cron-log-json'],

  setup() {
    const {
      fmtDisc,
      fmtDiscDelta,
      fmtKzt,
      fmtNum,
      discDeltaClass,
      discRowClass,
      discFmtDate,
      discFmtDateTime,
    } = useFormatters();

    return {
      fmtDisc,
      fmtDiscDelta,
      fmtKzt,
      fmtNum,
      discDeltaClass,
      discRowClass,
      discFmtDate,
      discFmtDateTime,
    };
  },

  data() {
    return {
      selectedMap: {},
      expandedMap: {},
      nameExpandedMap: {},
      activeTab: 'all',
      issueSubFilter: 'all',
      bulkMode: null,
      uspAggregate: false,
      cronLoading: { smeta: false, contracts: false },
      lastRun: { smeta: null, contracts: null },
      historyDialog: { show: false, logType: 'estimate', loading: false, rows: [] },
      jsonViewDialog: { show: false, loading: false, content: '' },
      worksDialog: { show: false, items: [], title: '' },
    };
  },

  computed: {
    isAllSelected() {
      const rows = this.discFilteredRows;
      return rows.length > 0 && rows.every(r => !!this.selectedMap[this.rowKey(r)]);
    },
    selectedRows() {
      return this.discFilteredRows.filter(r => !!this.selectedMap[this.rowKey(r)]);
    },
    applicableSelectedRows() {
      if (!this.bulkMode) return [];
      return this.selectedRows.filter(r => this.isRowApplicable(r));
    },
    modeApplicableCount() {
      if (!this.bulkMode) return 0;
      return this.discFilteredRows.filter(r => this.isRowApplicable(r)).length;
    },
    bulkActionLabel() {
      const a = this.applicableSelectedRows.length;
      const s = this.selectedRows.length;
      const cnt = a === s ? `${a}` : `${a} из ${s}`;
      if (this.bulkMode === 'smeta') return `Факт по смете (${cnt})`;
      if (this.bulkMode === 'usp') return `В УСП (${cnt})`;
      if (this.bulkMode === 'storno') return `Сторнировать (${cnt})`;
      if (this.bulkMode === 'smeta_to_usp') return `Смета → УСП (${cnt})`;
      return '';
    },
    bulkActionIcon() {
      if (this.bulkMode === 'smeta') return 'tune';
      if (this.bulkMode === 'usp') return 'send';
      if (this.bulkMode === 'storno') return 'undo';
      if (this.bulkMode === 'smeta_to_usp') return 'playlist_add_check';
      return '';
    },
    discTypeOptions() {
      const s = new Set();
      for (const r of this.data.rows) {
        if (r.project_type_name) s.add(r.project_type_name);
      }
      return [...s].sort();
    },

    discBlockOptions() {
      const s = new Set();
      for (const r of this.data.rows) {
        if (r.block_name) s.add(r.block_name);
      }
      return [...s].sort();
    },

    discFloorOptions() {
      const s = new Set();
      for (const r of this.data.rows) {
        if (r.level_num) s.add(r.level_num);
      }
      return [...s].sort((a, b) => a - b).map(v => ({ label: String(v), value: v }));
    },

    issueSubFilterOptions() {
      const issueRows = this.baseFilteredRows.filter(r => !this.isRowOk(r));
      let diffCount = 0, smetaDiffCount = 0, smetaDiffUspOkCount = 0, tripleCount = 0;
      let smetaVsUspSmetaCount = 0, onlyOurs = 0, onlyUsp = 0;
      for (const r of issueRows) {
        if (r.has_discrepancy) diffCount++;
        if (this.hasFactVsSmetaDiff(r)) smetaDiffCount++;
        if (this.hasFactVsSmetaDiff(r) && r.source === 'both' && !r.has_discrepancy) smetaDiffUspOkCount++;
        if (this.hasFactVsSmetaDiff(r) && r.has_discrepancy) tripleCount++;
        if (this.hasSmetaVsUspSmetaDiff(r)) smetaVsUspSmetaCount++;
        if (r.source === 'only_ours') onlyOurs++;
        if (r.source === 'only_usp') onlyUsp++;
      }
      return [
        { label: 'Все расхождения',              value: 'all',              icon: 'list',          iconColor: 'grey-7',   count: issueRows.length,    badgeColor: 'grey-3',   badgeText: 'grey-8' },
        { label: 'Факт ≠ УСП',                   value: 'diff',             icon: 'warning',       iconColor: 'orange-8', count: diffCount,           badgeColor: 'orange-2', badgeText: 'orange-10' },
        { label: 'Факт ≠ Наша смета',            value: 'smeta_diff',       icon: 'warning',       iconColor: 'red-7',    count: smetaDiffCount,      badgeColor: 'red-2',    badgeText: 'red-10' },
        { label: 'Смета ≠ Факт, но Факт = УСП',   value: 'smeta_diff_usp_ok', icon: 'info',       iconColor: 'blue-7',   count: smetaDiffUspOkCount, badgeColor: 'blue-2',   badgeText: 'blue-10', bgClass: 'bg-orange-1' },
        { label: 'Смета ≠ Факт И Факт ≠ УСП',     value: 'triple_diff',      icon: 'error',        iconColor: 'red-10',   count: tripleCount,         badgeColor: 'red-1',    badgeText: 'red-10',  bgClass: 'bg-red-1' },
        { label: 'Наша смета ≠ Смета УСП',        value: 'smeta_vs_usp_smeta', icon: 'unfold_less', iconColor: 'green-8',  count: smetaVsUspSmetaCount, badgeColor: 'green-2',  badgeText: 'green-10' },
        { label: 'Только у нас',                   value: 'only_ours',        icon: 'arrow_back',    iconColor: 'blue-5',   count: onlyOurs,            badgeColor: 'blue-1',   badgeText: 'blue-10' },
        { label: 'Только в УСП',                   value: 'only_usp',         icon: 'arrow_forward', iconColor: 'purple-5', count: onlyUsp,             badgeColor: 'purple-1', badgeText: 'purple-10' },
      ];
    },

    baseFilteredRows() {
      let rows = this.data.rows;
      const { filterType, filterBlock, filterFloor, search } = this.data;
      if (filterType) rows = rows.filter(r => r.project_type_name === filterType);
      if (filterBlock) rows = rows.filter(r => r.block_name === filterBlock);
      if (filterFloor != null) rows = rows.filter(r => r.level_num === filterFloor);
      if (search) {
        const q = search.toLowerCase();
        rows = rows.filter(r =>
          (r.work_set_name || '').toLowerCase().includes(q) ||
          (r.work_guid || '').toLowerCase().includes(q) ||
          (r.group_name || '').toLowerCase().includes(q)
        );
      }
      return rows;
    },

    discIssuesCount() {
      return this.baseFilteredRows.filter(r => !this.isRowOk(r)).length;
    },

    discOkCount() {
      return this.baseFilteredRows.filter(r => this.isRowOk(r)).length;
    },

    multiProjectCount() {
      return this.baseFilteredRows.filter(r => (r.proj_cnt || 0) > 1).length;
    },

    discFilteredRows() {
      let rows = this.baseFilteredRows;
      if (this.activeTab === 'multi') {
        rows = rows.filter(r => (r.proj_cnt || 0) > 1);
      } else if (this.activeTab === 'ok') {
        rows = rows.filter(r => this.isRowOk(r));
      } else if (this.activeTab === 'issues') {
        rows = rows.filter(r => !this.isRowOk(r));
        const sf = this.issueSubFilter;
        if (sf === 'diff') rows = rows.filter(r => r.has_discrepancy);
        else if (sf === 'smeta_diff') rows = rows.filter(r => this.hasFactVsSmetaDiff(r));
        else if (sf === 'smeta_diff_usp_ok') rows = rows.filter(r =>
          this.hasFactVsSmetaDiff(r) && r.source === 'both' && !r.has_discrepancy
        );
        else if (sf === 'triple_diff') rows = rows.filter(r =>
          r.has_discrepancy && this.hasFactVsSmetaDiff(r)
        );
        else if (sf === 'smeta_vs_usp_smeta') rows = rows.filter(r => this.hasSmetaVsUspSmetaDiff(r));
        else if (sf === 'only_ours') rows = rows.filter(r => r.source === 'only_ours');
        else if (sf === 'only_usp') rows = rows.filter(r => r.source === 'only_usp');
      }
      return rows;
    },

    discSummary() {
      const all = this.data.rows;
      const rows = this.discFilteredRows;
      let our_smr = 0, our_mat = 0, our_total = 0;
      let osm_smr = 0, osm_mat = 0, osm_total = 0;
      let usm_smr = 0, usm_mat = 0, usm_total = 0;
      let usp_smr = 0, usp_mat = 0, usp_total = 0;
      let d_smr = 0, d_mat = 0, d_total = 0;
      for (const r of rows) {
        our_smr   += r.our_smr            || 0;
        our_mat   += r.our_materials      || 0;
        our_total += r.our_total          || 0;
        osm_smr   += r.our_smeta_smr     || 0;
        osm_mat   += r.our_smeta_mat     || 0;
        osm_total += r.our_smeta_total   || 0;
        usm_smr   += r.usp_smeta_smr    || 0;
        usm_mat   += r.usp_smeta_mat    || 0;
        usm_total += r.usp_smeta_total  || 0;
        usp_smr   += r.usp_smr           || 0;
        usp_mat   += r.usp_materials     || 0;
        usp_total += r.usp_total         || 0;
        d_smr     += r.delta_smr         || 0;
        d_mat     += r.delta_materials   || 0;
        d_total   += r.delta_total       || 0;
      }
      let diffCount = 0, smetaDiffCount = 0, smetaDiffUspOkCount = 0;
      let smetaVsUspSmetaCount = 0, matchCount = 0, onlyOurs = 0, onlyUsp = 0;
      for (const r of all) {
        if (r.has_discrepancy) diffCount++;
        if (this.hasFactVsSmetaDiff(r)) smetaDiffCount++;
        if (this.hasFactVsSmetaDiff(r) && r.source === 'both' && !r.has_discrepancy) smetaDiffUspOkCount++;
        if (this.hasSmetaVsUspSmetaDiff(r)) smetaVsUspSmetaCount++;
        if (this.isRowOk(r)) matchCount++;
        if (r.source === 'only_ours') onlyOurs++;
        if (r.source === 'only_usp') onlyUsp++;
      }
      return {
        our_smr, our_mat, our_total,
        osm_smr, osm_mat, osm_total,
        usm_smr, usm_mat, usm_total,
        usp_smr, usp_mat, usp_total,
        d_smr, d_mat, d_total,
        count: rows.length,
        diffCount, smetaDiffCount, smetaDiffUspOkCount, smetaVsUspSmetaCount,
        matchCount, onlyOurs, onlyUsp,
      };
    },
  },

  watch: {
    activeTab(val) {
      if (val !== 'issues') this.issueSubFilter = 'all';
    },
    bulkMode() {
      this.selectedMap = {};
    },
  },

  methods: {
    isRowOk(r) {
      return r.source === 'both'
        && !r.has_discrepancy
        && !this.hasFactVsSmetaDiff(r)
        && !this.hasSmetaVsUspSmetaDiff(r);
    },
    hasFactVsSmetaDiff(r) {
      return (r.our_smeta_total || 0) > 0
        && Math.abs((r.our_total || 0) - (r.our_smeta_total || 0)) > 1;
    },
    hasSmetaVsUspSmetaDiff(r) {
      // Сметы сравнимы только если обе есть.
      return (r.our_smeta_total || 0) > 0
        && (r.usp_smeta_total || 0) > 0
        && (
          Math.abs((r.our_smeta_smr || 0) - (r.usp_smeta_smr || 0)) > 1
          || Math.abs((r.our_smeta_mat || 0) - (r.usp_smeta_mat || 0)) > 1
        );
    },
    rowKey(r) {
      return r.work_guid || JSON.stringify(r.combos || []);
    },
    parsePct(groupName) {
      if (!groupName) return null;
      const m = groupName.match(/Процент:\s*([\d.]+)/);
      return m ? parseFloat(m[1]) : null;
    },
    parseGroupLabel(groupName) {
      if (!groupName) return '';
      return groupName.replace(/Процент:\s*[\d.]+\s*/, '').trim();
    },
    pctClass(pct) {
      if (pct == null) return 'disc-pct--none';
      if (pct > 100) return 'disc-pct--over';
      if (pct >= 99.9) return 'disc-pct--done';
      if (pct > 0) return 'disc-pct--partial';
      return 'disc-pct--zero';
    },
    isMultiProject(r) {
      return (r.proj_cnt || 0) > 1 && Array.isArray(r.project_breakdown) && r.project_breakdown.length > 1;
    },
    isExpanded(r) {
      return !!this.expandedMap[this.rowKey(r)];
    },
    toggleExpand(r) {
      const key = this.rowKey(r);
      const copy = { ...this.expandedMap };
      if (copy[key]) delete copy[key];
      else copy[key] = true;
      this.expandedMap = copy;
    },
    isNameExpanded(r) {
      return !!this.nameExpandedMap[this.rowKey(r)];
    },
    toggleNameExpand(r) {
      const key = this.rowKey(r);
      const copy = { ...this.nameExpandedMap };
      if (copy[key]) delete copy[key];
      else copy[key] = true;
      this.nameExpandedMap = copy;
    },
    openWorksDialog(r) {
      this.worksDialog.items = this.workSetItems(r);
      this.worksDialog.title = this.parseGroupLabel(r?.group_name) || (r?.work_guid || '');
      this.worksDialog.show = true;
    },
    workSetItems(r) {
      const arr = r && r.work_set_breakdown;
      if (Array.isArray(arr) && arr.length) return arr;
      const legacy = r && r.work_set_names_arr;
      if (Array.isArray(legacy) && legacy.length) {
        return legacy.map(n => ({ work_set_name: n }));
      }
      const raw = (r && r.work_set_name) || '';
      const cleaned = raw.replace(/\s+floor_map_id:\s*\d+\s*$/i, '');
      if (!cleaned) return [];
      return cleaned.split(',').map(s => s.trim()).filter(Boolean)
        .map(n => ({ work_set_name: n }));
    },
    workSetNames(r) {
      return this.workSetItems(r).map(it => it.work_set_name).filter(Boolean);
    },
    workSetShortName(r) {
      const names = this.workSetNames(r);
      if (!names.length) return '—';
      if (names.length === 1) return names[0];
      return `${names[0]} (+${names.length - 1})`;
    },
    workSetFullTitle(r) {
      const names = this.workSetNames(r);
      if (!names.length) return r?.work_set_name || '';
      return names.join('\n');
    },
    toggleRow(r) {
      const key = this.rowKey(r);
      const copy = { ...this.selectedMap };
      if (copy[key]) {
        delete copy[key];
      } else {
        copy[key] = true;
      }
      this.selectedMap = copy;
    },
    toggleAll(val) {
      const copy = { ...this.selectedMap };
      for (const r of this.discFilteredRows) {
        const key = this.rowKey(r);
        if (val) copy[key] = true;
        else delete copy[key];
      }
      this.selectedMap = copy;
    },
    isRowApplicable(r) {
      if (this.bulkMode === 'smeta') return this.hasSmetaDiff(r);
      if (this.bulkMode === 'usp') return r.has_discrepancy || r.source === 'only_ours';
      if (this.bulkMode === 'storno') return r.source === 'both';
      if (this.bulkMode === 'smeta_to_usp') return !!r.work_guid && (r.our_smeta_total || 0) > 0;
      return false;
    },
    executeBulkAction() {
      const rows = this.applicableSelectedRows;
      if (!rows.length) return;
      if (this.bulkMode === 'smeta') this.$emit('align-to-smeta-bulk', rows);
      else if (this.bulkMode === 'usp') this.$emit('align-to-usp-bulk', rows, { perPeriod: !this.uspAggregate });
      else if (this.bulkMode === 'storno') this.$emit('storno-bulk', rows);
      else if (this.bulkMode === 'smeta_to_usp') this.$emit('send-smeta-bulk', rows);
    },
    hasSmetaDiff(r) {
      if ((r.our_smeta_total || 0) <= 0) return false;
      const moneyDiff = Math.abs((r.our_smr || 0) - (r.our_smeta_smr || 0)) > 0 ||
        Math.abs((r.our_materials || 0) - (r.our_smeta_mat || 0)) > 0;
      const volDiff = (r.smeta_vol || 0) > 0 &&
        Math.abs((r.fact_vol || 0) - (r.smeta_vol || 0)) > 0;
      return moneyDiff || volDiff;
    },
    smetaDeltaClass(val) {
      if (val == null || Math.abs(val) <= 1) return 'disc-smeta-delta--zero';
      return val > 0 ? 'disc-smeta-delta--over' : 'disc-smeta-delta--under';
    },
    fmtHistDt(val) {
      if (!val) return '—';
      const d = new Date(val);
      return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    },
    copyJson() {
      navigator.clipboard.writeText(this.jsonViewDialog.content).then(() => {
        this.$q.notify({ message: 'Скопировано', color: 'positive', timeout: 1000, position: 'top' });
      });
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$q.notify({ message: 'Скопировано', color: 'positive', timeout: 1000, position: 'top' });
      }).catch(() => {
        this.$q.notify({ message: 'Не удалось скопировать', color: 'negative', timeout: 1500, position: 'top' });
      });
    },
  },
};
</script>

<style scoped>
.disc-card {
  display: flex;
  flex-direction: column;
  height: 100vh;
}
.disc-main-bar {
  height: auto !important;
  min-height: 44px;
  padding: 4px 8px;
}
.disc-main-titles {
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-left: 8px;
  flex: 1;
  line-height: 1.2;
}
.disc-main-project {
  font-size: 11px;
  opacity: 0.85;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.disc-main-title {
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.disc-filters {
  flex-shrink: 0;
  background: #fafafa;
}
.disc-tabs-row {
  flex-shrink: 0;
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
}
.disc-tabs {
  min-height: 32px;
}
.disc-tabs :deep(.q-tab) {
  min-height: 32px;
  padding: 0 12px;
  font-size: 13px;
}
.disc-mode-toggle {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
.disc-mode-toggle :deep(.q-btn) {
  font-size: 11px !important;
  padding: 2px 8px !important;
  min-height: 26px !important;
}
.disc-scroll {
  flex: 1;
  overflow: auto;
  min-height: 0;
}
.disc-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  border-spacing: 0;
}
.disc-table th,
.disc-table td {
  font-size: 12px;
  padding: 5px 8px;
  vertical-align: top;
  border: 1px solid #e0e0e0;
}
.disc-table thead tr:first-child th {
  position: sticky;
  top: 0;
  z-index: 3;
  white-space: nowrap;
  text-align: center;
}
.disc-table thead tr:nth-child(2) th {
  position: sticky;
  top: 28px;
  z-index: 3;
  white-space: nowrap;
  text-align: center;
}
.disc-th-check {
  width: 32px !important;
  min-width: 32px;
  max-width: 32px;
  padding: 0 4px !important;
  text-align: center;
}
.disc-cell-check {
  width: 32px;
  min-width: 32px;
  max-width: 32px;
  padding: 0 4px !important;
  text-align: center;
  vertical-align: middle;
}
.disc-th-info {
  background: #eceff1;
  color: #37474f;
  font-weight: 600;
  border-bottom: 2px solid #b0bec5;
}
.disc-th-operfact {
  background: #ede7f6;
  color: #311b92;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #b39ddb;
}
.disc-th-ours {
  background: #e3f2fd;
  color: #0d47a1;
  font-weight: 600;
  border-bottom: 2px solid #64b5f6;
}
.disc-th-usp {
  background: #f3e5f5;
  color: #4a148c;
  font-weight: 600;
  border-bottom: 2px solid #ba68c8;
}
.disc-th-smeta-group {
  background: #e0f7fa;
  color: #006064;
  font-weight: 700;
  border-bottom: 1px solid #b2ebf2;
}
.disc-th-smeta {
  background: #e0f7fa;
  color: #006064;
  font-weight: 600;
  border-bottom: 2px solid #4dd0e1;
}
.disc-th-smeta-usp {
  background: #e8f5e9;
  color: #1b5e20;
  font-weight: 600;
  border-bottom: 2px solid #81c784;
}
.disc-th-delta {
  background: #fff3e0;
  color: #e65100;
  font-weight: 600;
  border-bottom: 2px solid #ffb74d;
}

/* --- Объект --- */
.disc-cell-obj {
  vertical-align: middle;
}
.disc-obj-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
}
.disc-obj-icon {
  flex-shrink: 0;
  margin-top: 1px;
}
.disc-obj-type {
  font-size: 11px;
  font-weight: 500;
  color: #37474f;
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.disc-obj-meta {
  font-size: 10px;
  color: #78909c;
  margin-top: 2px;
  padding-left: 18px;
}
.disc-obj-multi {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-top: 4px;
  margin-left: 18px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 600;
  color: #bf360c;
  background: #fff3e0;
  border: 1px solid #ffcc80;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s;
}
.disc-obj-multi:hover {
  background: #ffe0b2;
}

/* --- Работа --- */
.disc-cell-work {
  line-height: 1.3;
  vertical-align: middle;
}
.disc-work-pct-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}
.disc-work-pct {
  flex-shrink: 0;
  display: inline-block;
  min-width: 38px;
  text-align: center;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2px;
}
.disc-pct--done   { background: #c8e6c9; color: #1b5e20; }
.disc-pct--partial { background: #fff9c4; color: #f57f17; }
.disc-pct--zero   { background: #eceff1; color: #78909c; }
.disc-pct--over   { background: #ffcdd2; color: #b71c1c; }
.disc-pct--none   { background: #eceff1; color: #b0bec5; }
.disc-work-name {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: #263238;
  line-height: 1.3;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.disc-works-btn {
  flex-shrink: 0;
  min-height: 18px;
  padding: 0 6px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  border-radius: 9px;
  background: #eceff1;
}
.disc-works-btn :deep(.q-icon) {
  font-size: 13px;
  margin-right: 2px;
}
.disc-works-btn:hover {
  background: #cfd8dc;
}
.disc-work-mini-tbl {
  margin: 6px 0 4px 0;
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  color: #263238;
  line-height: 1.35;
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
}
.disc-work-mini-tbl thead th {
  background: #eceff1;
  color: #546e7a;
  font-weight: 600;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  text-align: left;
  padding: 3px 6px;
  border-bottom: 1px solid #cfd8dc;
}
.disc-work-mini-tbl tbody td {
  padding: 2px 6px;
  vertical-align: top;
  border-bottom: 1px solid #eceff1;
}
.disc-work-mini-tbl tbody tr:last-child td {
  border-bottom: none;
}
.disc-work-mini-tbl tbody tr:hover td {
  background: #f1f8e9;
}
.dwm-th-num, .dwm-td-num {
  width: 22px;
  text-align: right;
  color: #90a4ae;
  font-variant-numeric: tabular-nums;
}
.dwm-th-id, .dwm-td-id {
  width: 50px;
  font-family: 'Roboto Mono', monospace;
  color: #607d8b;
  font-variant-numeric: tabular-nums;
}
.dwm-td-name {
  word-break: break-word;
}
.dwm-th-fm, .dwm-td-fm {
  width: 220px;
  font-family: 'Roboto Mono', monospace;
  font-size: 10px;
  color: #607d8b;
  font-variant-numeric: tabular-nums;
  word-break: break-word;
}
.dwm-fm-cnt {
  display: inline-block;
  margin-right: 4px;
  padding: 0 4px;
  background: #cfd8dc;
  color: #37474f;
  border-radius: 8px;
  font-family: inherit;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.2px;
}
.disc-work-group-name {
  font-size: 10px;
  color: #78909c;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.25;
}
.disc-work-guid {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-top: 2px;
  padding: 0 4px;
  font-family: 'Roboto Mono', monospace;
  font-size: 10px;
  color: #90a4ae;
  background: #f5f5f5;
  border-radius: 3px;
  cursor: default;
}
.disc-detail-btn,
.disc-name-toggle-btn {
  flex-shrink: 0;
  margin-top: -2px;
}
.disc-detail-btn :deep(.q-icon),
.disc-name-toggle-btn :deep(.q-icon) {
  font-size: 16px;
}
.disc-guid-copy {
  font-size: 10px !important;
  min-height: 14px !important;
}
.disc-guid-copy :deep(.q-icon) {
  font-size: 12px;
}

/* --- Деньги --- */
.disc-cell-money {
  text-align: right;
  vertical-align: middle;
}
.disc-money-total {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.disc-money-detail {
  display: flex;
  flex-direction: column;
  margin-top: 2px;
}
.disc-money-detail span {
  font-size: 10px;
  color: #90a4ae;
  white-space: nowrap;
}

/* Дельта смета-факт */
.disc-smeta-delta-block {
  margin-top: 3px;
  padding-top: 3px;
  border-top: 1px dashed #cfd8dc;
  display: flex;
  flex-direction: column;
}
.disc-smeta-delta-block span {
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}
.disc-align-btn {
  margin-top: 2px;
  font-size: 10px !important;
}
.disc-smeta-delta--over {
  color: #e65100;
}
.disc-smeta-delta--under {
  color: #c62828;
}
.disc-smeta-delta--zero {
  color: #43a047;
}

/* Фоны ячеек */
.disc-cell-ours      { background: #f5f9ff; }
.disc-cell-smeta     { background: #f0fbfc; }
.disc-cell-smeta-usp { background: #f1f8f2; }
.disc-cell-usp       { background: #faf5fc; }

/* --- Строки --- */
.disc-table tbody tr:hover td {
  filter: brightness(0.97);
}
.disc-row-ok { }
.disc-row-diff {
  background: #fff8e1;
}
.disc-row-only-ours {
  background: #e8f0fe;
}
.disc-row-only-usp {
  background: #f3e5f5;
}

/* --- Разбивка по проектам (child row) --- */
.disc-breakdown-row > td {
  padding: 0 !important;
  background: #fff3e0;
  border-top: 2px solid #ffb74d;
  border-bottom: 2px solid #ffb74d;
}
.disc-breakdown-row:hover td {
  filter: none !important;
}
.disc-breakdown {
  padding: 8px 16px 10px 50px;
}
.disc-breakdown-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #bf360c;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}
.disc-breakdown-guid {
  font-family: 'Roboto Mono', monospace;
  font-size: 11px;
  color: #6d4c41;
  background: rgba(255, 255, 255, 0.5);
  padding: 0 6px;
  border-radius: 3px;
}
.disc-breakdown-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
}
.disc-breakdown-table th,
.disc-breakdown-table td {
  font-size: 11px;
  padding: 4px 8px;
  border: 1px solid #ffe0b2;
  text-align: right;
  vertical-align: middle;
}
.disc-breakdown-table th {
  background: #ffecb3;
  color: #e65100;
  font-weight: 600;
  text-align: center;
}
.disc-breakdown-table th:first-child,
.disc-breakdown-table td:first-child {
  text-align: left;
}
.disc-bt-smeta { background: #f0fbfc; color: #006064; }
.disc-bt-fact  { background: #f5f9ff; color: #0d47a1; }
.disc-bt-delta { background: #fff8e1; color: #bf360c; font-weight: 600; }
.disc-bd-type {
  font-size: 11px;
  font-weight: 600;
  color: #37474f;
}
.disc-bd-meta {
  font-size: 10px;
  color: #78909c;
  margin-top: 1px;
}
.disc-bd-sep {
  color: #bdbdbd;
  margin: 0 2px;
}
.disc-bd-pid {
  font-family: 'Roboto Mono', monospace;
  text-align: center !important;
  color: #546e7a;
}


/* --- Футер --- */
.disc-footer {
  flex-shrink: 0;
  border-top: 2px solid #bf360c;
  background: #fafafa;
  padding: 6px 12px;
}
.disc-footer-row {
  display: flex;
  align-items: center;
  gap: 20px;
  font-size: 12px;
  flex-wrap: wrap;
}
.disc-footer-label {
  font-weight: 700;
  font-size: 13px;
  color: #37474f;
  white-space: nowrap;
}
.disc-footer-group {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
}
.disc-footer-title {
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.disc-footer-ours {
  background: #e3f2fd;
  color: #0d47a1;
}
.disc-footer-smeta {
  background: #e0f7fa;
  color: #006064;
}
.disc-footer-smeta-usp {
  background: #e8f5e9;
  color: #1b5e20;
}
.disc-footer-usp {
  background: #f3e5f5;
  color: #4a148c;
}
.disc-footer-delta {
  background: #fff3e0;
  color: #e65100;
}

.cron-btn-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
}
.cron-last-run {
  display: flex;
  align-items: center;
  gap: 3px;
  padding-left: 2px;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  border-radius: 3px;
  padding: 1px 5px;
}
.cron-last-run--ok  { background: #e0f2f1; color: #00695c; }
.cron-last-run--err { background: #ffebee; color: #c62828; }

.cron-hist-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.cron-hist-table th {
  background: #f5f5f5;
  font-weight: 600;
  padding: 5px 8px;
  border: 1px solid #e0e0e0;
  text-align: center;
  white-space: nowrap;
}
.cron-hist-table td {
  padding: 4px 8px;
  border: 1px solid #e0e0e0;
  vertical-align: middle;
}
.cron-hist-ok { background: #f1f8f2; }
.cron-hist-err { background: #fff5f5; }
.cron-hist-error {
  max-width: 200px;
  word-break: break-word;
  color: #c62828;
}
.cron-hist-response {
  max-width: 240px;
  word-break: break-word;
  color: #37474f;
  font-family: 'Roboto Mono', monospace;
  font-size: 11px;
}
.cron-json-pre {
  font-family: 'Roboto Mono', monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  margin: 0;
  color: #263238;
}
</style>
